import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion'
import styles from './mention-extension.module.scss'
import { MentionPluginKey } from './mentionPluginKey'

export interface MentionOptions {
    HTMLAttributes: Record<string, unknown>
    renderLabel: (props: { options: MentionOptions; node: ProseMirrorNode }) => string
    suggestion: Partial<SuggestionOptions>
    mentionClass?: string
    userMentionClass?: string
    modelMentionClass?: string
    referenceMentionClass?: string
    onMentionClick?: (mentionId: string, event: Event, element: HTMLElement) => void
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        mention: {
            /**
             * Insert a mention
             */
            insertMention: (attributes: {
                id: string
                label?: string
                type?: string
                denotationChar?: string
                content?: string
                link?: string
                kind?: string
                previewUrl?: string | null
                url?: string | null
                contentType?: string | null
            }) => ReturnType
        }
    }
}

export { MentionPluginKey } from './mentionPluginKey'

export const KnovMention = Node.create<MentionOptions>({
    name: 'mention',

    addOptions() {
        return {
            HTMLAttributes: {},
            renderLabel({ options, node }) {
                const mention = node.attrs
                return `${mention.denotationChar || '@'}${
                    mention.label || mention.content || mention.id
                }`
            },
            suggestion: {
                char: '@',
                pluginKey: MentionPluginKey,
                command: ({ editor, range, props }) => {
                    // Remove the trigger character and any search text
                    editor.chain().focus().deleteRange(range).insertMention(props).run()
                },
            },
        }
    },

    group: 'inline',

    inline: true,

    selectable: false,

    atom: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-id'),
                renderHTML: attributes => ({
                    'data-mention-id': attributes.id,
                }),
            },
            label: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-label'),
                renderHTML: attributes => ({
                    'data-mention-label': attributes.label,
                }),
            },
            type: {
                default: 'user',
                parseHTML: element => element.getAttribute('data-mention-type'),
                renderHTML: attributes => ({
                    'data-mention-type': attributes.type,
                }),
            },
            denotationChar: {
                default: '@',
                parseHTML: element => element.getAttribute('data-mention-char'),
                renderHTML: attributes => ({
                    'data-mention-char': attributes.denotationChar,
                }),
            },
            content: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-content'),
                renderHTML: attributes => ({
                    'data-mention-content': attributes.content,
                }),
            },
            link: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-link'),
                renderHTML: attributes => ({
                    'data-mention-link': attributes.link,
                }),
            },
            kind: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-kind'),
                renderHTML: attributes => ({
                    'data-mention-kind': attributes.kind,
                }),
            },
            previewUrl: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-preview-url'),
                renderHTML: attributes => ({
                    'data-mention-preview-url': attributes.previewUrl,
                }),
            },
            url: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-url'),
                renderHTML: attributes => ({
                    'data-mention-url': attributes.url,
                }),
            },
            contentType: {
                default: null,
                parseHTML: element => element.getAttribute('data-mention-content-type'),
                renderHTML: attributes => ({
                    'data-mention-content-type': attributes.contentType,
                }),
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-mention-id]',
            },
        ]
    },

    renderHTML({ node, HTMLAttributes }) {
        const mention = node.attrs
        const isModel = mention.type === 'model' || mention.denotationChar === '@@'
        const isReference = mention.type === 'reference'
        const mentionLabel = mention.content || mention.label || mention.id
        const referenceText = `${mention.denotationChar || '@'}${String(mentionLabel).replace(
            /^@+/,
            '',
        )}`

        // Use CSS module classes from options, fallback to default strings
        const mentionClass = this.options.mentionClass || 'mention'
        const typeClass = isReference
            ? this.options.referenceMentionClass || 'reference-mention'
            : isModel
            ? this.options.modelMentionClass || 'model-mention'
            : this.options.userMentionClass || 'user-mention'

        // Create click handler for user mentions
        const attrs: Record<string, string | null | undefined> = {
            class: `${mentionClass} ${typeClass}`,
            'data-mention-id': mention.id,
            'data-mention-type': mention.type || 'user',
            'data-mention-char': mention.denotationChar || '@',
            'data-mention-content': mentionLabel,
            'data-mention-link': mention.link,
            'data-mention-kind': mention.kind,
            'data-mention-preview-url': mention.previewUrl,
            'data-mention-url': mention.url,
            'data-mention-content-type': mention.contentType,
        }

        // Add clickable class for user mentions only
        if (!isModel && this.options.onMentionClick) {
            attrs.class = `${attrs.class} ${styles.clickableMention}`
        }

        if (isReference) {
            const fallbackLabel =
                mention.kind === 'video' ? 'V' : mention.kind === 'audio' ? 'A' : 'I'
            return [
                'span',
                mergeAttributes(attrs, this.options.HTMLAttributes, HTMLAttributes),
                [
                    'span',
                    { class: 'prompt-reference-mention-preview' },
                    mention.previewUrl && mention.kind !== 'audio'
                        ? ['img', { src: mention.previewUrl, alt: '' }]
                        : ['span', { class: 'prompt-reference-mention-fallback' }, fallbackLabel],
                ],
                ['span', { class: 'prompt-reference-mention-text' }, referenceText],
            ]
        }

        return [
            'span',
            mergeAttributes(attrs, this.options.HTMLAttributes, HTMLAttributes),
            this.options.renderLabel({
                options: this.options,
                node,
            }),
        ]
    },

    renderText({ node }) {
        const mention = node.attrs
        return `${mention.denotationChar || '@'}${mention.label || mention.content || mention.id}`
    },

    addCommands() {
        return {
            insertMention:
                attributes =>
                ({ chain }) => {
                    return chain()
                        .focus()
                        .insertContent({
                            type: this.name,
                            attrs: attributes,
                        })
                        .run()
                },
        }
    },

    addProseMirrorPlugins() {
        const { onMentionClick } = this.options

        return [
            Suggestion({
                editor: this.editor,
                ...this.options.suggestion,
            }),

            // Mention click handler plugin - using DOM events for better compatibility
            new Plugin({
                key: new PluginKey('mentionClickHandler'),

                view(editorView) {
                    const handleMentionClick = (event: Event) => {
                        const target = event.target as HTMLElement

                        // Check if clicked element or its parent is a mention span
                        let mentionElement = target
                        if (!mentionElement.hasAttribute('data-mention-id')) {
                            mentionElement = target.closest('[data-mention-id]') as HTMLElement
                        }

                        if (mentionElement && mentionElement.hasAttribute('data-mention-id')) {
                            const mentionId = mentionElement.getAttribute('data-mention-id')
                            const mentionType =
                                mentionElement.getAttribute('data-mention-type') || 'user'

                            // Only handle user mentions, not model mentions
                            if (mentionType !== 'model' && mentionId) {
                                event.preventDefault()
                                event.stopPropagation()

                                if (onMentionClick) {
                                    onMentionClick(mentionId, event, mentionElement)
                                }

                                return true
                            }
                        }

                        return false
                    }

                    // Add click listener to the editor DOM
                    const editorDOM = editorView.dom
                    editorDOM.addEventListener('click', handleMentionClick, true) // Use capture phase

                    return {
                        destroy() {
                            editorDOM.removeEventListener('click', handleMentionClick, true)
                        },
                    }
                },
            }),
        ]
    },
})
